import axios, { type AxiosResponse } from 'axios';
import type { StoryResponse, StoryListItem, StoryListResponse, PublicStoryResponse, PublicLibraryResponse, StyleOption, PageCountOption, CreateStoryData, PurchaseHistoryResponse, BalanceResponse, PurchaseValidateResponse, PageReadSubmit, PageReadResponse, QuizAnswerSubmit, DashboardData, TokenResponse, AnonStoryCreateData, AnonStoryResponse, AuthConfigResponse , AnonStatus, TeaserView } from './types';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CloudFront URL for direct image/audio access (much faster than API redirects)
const CLOUDFRONT_URL: string = process.env.REACT_APP_CLOUDFRONT_URL || 'https://dosr4ls3fur6u.cloudfront.net';
const S3_IMAGE_PATH = 'cloud-front/images';
const S3_AUDIO_PATH = 'cloud-front/audio';

// Centralized error message extraction
export function getErrorMessage(error: unknown, fallback = 'Ein Fehler ist aufgetreten'): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { detail?: string } } };
    return axiosError.response?.data?.detail || fallback;
  }
  return fallback;
}

// Namespaced localStorage keys (must match AuthContext)
const TOKEN_KEY = 'snugtale_token';
const REFRESH_KEY = 'snugtale_refreshToken';

// 401 interceptor — silently refresh access token and retry
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Don't retry refresh endpoint itself, or already-retried requests
    if (error.response?.status !== 401 || originalRequest._retry ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/google') ||
        originalRequest.url?.includes('/auth/apple')) {
      if (error.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
      }
      return Promise.reject(error);
    }

    // Try to refresh
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) {
      localStorage.removeItem(TOKEN_KEY);
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request while another refresh is in progress
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            resolve(axios(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(`${API_URL}/auth/refresh`, {
        refresh_token: refreshToken,
      });
      const newToken = data.access_token;
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(REFRESH_KEY, data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      processQueue(null, newToken);
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Direct CloudFront URLs for fast loading
export const getDirectImageUrl = (storyId: string, pageType: string, pageNumber = 0, thumbSize?: 256 | 512): string => {
  const suffix = thumbSize ? `_thumb${thumbSize}` : '';
  if (pageType === 'cover') {
    return `${CLOUDFRONT_URL}/${S3_IMAGE_PATH}/${storyId}_cover${suffix}.webp`;
  }
  return `${CLOUDFRONT_URL}/${S3_IMAGE_PATH}/${storyId}_page${pageNumber}${suffix}.webp`;
};

export const getDirectAudioUrl = (storyId: string, pageNumber: number, voice = 'nova'): string => {
  return `${CLOUDFRONT_URL}/${S3_AUDIO_PATH}/${storyId}_page${pageNumber}_${voice}.opus`;
};

// Stories API
export const storiesApi = {
  list: (page = 1, pageSize = 12): Promise<AxiosResponse<StoryListResponse>> =>
    axios.get(`${API_URL}/stories?page=${page}&page_size=${pageSize}`),
  get: (storyId: string): Promise<AxiosResponse<StoryResponse>> => axios.get(`${API_URL}/stories/${storyId}`),
  getCoverImageUrl: (storyId: string): string => getDirectImageUrl(storyId, 'cover'),
  getPageImageUrl: (storyId: string, pageNumber: number): string => getDirectImageUrl(storyId, 'page', pageNumber),
  create: (storyData: CreateStoryData): Promise<AxiosResponse<StoryResponse>> => axios.post(`${API_URL}/stories`, storyData),
  delete: (storyId: string): Promise<AxiosResponse<void>> => axios.delete(`${API_URL}/stories/${storyId}`),
  generatePageImage: (storyId: string, pageNumber: number): Promise<AxiosResponse<{ image_url: string }>> =>
    axios.post(`${API_URL}/stories/${storyId}/pages/${pageNumber}/generate-image`),
  generateAllImages: (storyId: string): Promise<AxiosResponse<{ generated_count: number }>> =>
    axios.post(`${API_URL}/stories/${storyId}/generate-all-images`),
  regenerateAllImages: (storyId: string): Promise<AxiosResponse<{ generated_count: number }>> =>
    axios.post(`${API_URL}/stories/${storyId}/regenerate-all-images`),
  regenerateAllExistingStories: (): Promise<AxiosResponse<void>> =>
    axios.post(`${API_URL}/stories/regenerate-all-existing`),
  repairMedia: (storyId: string): Promise<AxiosResponse<{ queued_images: number; queued_audio: number }>> =>
    axios.post(`${API_URL}/stories/${storyId}/repair-media`),
  generatePageAudio: (storyId: string, pageNumber: number, voice = 'nova'): Promise<AxiosResponse<{ audio_url: string }>> =>
    axios.post(`${API_URL}/stories/${storyId}/pages/${pageNumber}/audio?voice=${voice}`),
  toggleShare: (storyId: string): Promise<AxiosResponse<{ is_public: boolean; share_token: string | null }>> => axios.post(`${API_URL}/stories/${storyId}/share`),
  // Library listing (2026-09-11): `is_public` = shown in the public library; the link (share_token) is separate.
  setListing: (storyId: string, listed: boolean): Promise<AxiosResponse<{ is_public: boolean; share_token: string | null }>> => axios.put(`${API_URL}/stories/${storyId}/listing`, { listed }),
  retry: (storyId: string): Promise<AxiosResponse<{ status: string; story_id: string }>> =>
    axios.post(`${API_URL}/stories/${storyId}/retry`),
  // Printable PDFs (2026-09-14): both variants at once, rendered on first request and cached on S3/CloudFront.
  getPdf: (storyId: string): Promise<AxiosResponse<{ a4: string; booklet: string; cached: boolean }>> =>
    axios.get(`${API_URL}/stories/${storyId}/pdf`),
  // Whole narration as one MP3 for Toniebox / Yoto (2026-09-16): rendered on first request, cached on S3.
  getAudioExport: (storyId: string): Promise<AxiosResponse<{ url: string; cached: boolean; pages: number }>> =>
    axios.get(`${API_URL}/stories/${storyId}/audio-export`),
  // Link an anonymous trial story to the logged-in account (lib/anonStory.ts)
  claim: (claimToken: string): Promise<AxiosResponse<{ story_id: string }>> =>
    axios.post(`${API_URL}/stories/claim`, { claim_token: claimToken }),
  // Claim by the code the parent typed (2026-09-17) — into the app or any other browser.
  claimByCode: (claimCode: string): Promise<AxiosResponse<{ story_id: string }>> =>
    axios.post(`${API_URL}/stories/claim`, { claim_code: claimCode }),
  screenTheme: (theme: string, language: string): Promise<AxiosResponse<{ ok: boolean; category: string | null }>> =>
    axios.post(`${API_URL}/stories/screen-theme`, { theme, language }),
  softenTheme: (theme: string, category: string | null, language: string, hint: string | null = null): Promise<AxiosResponse<{ softened: string }>> =>
    axios.post(`${API_URL}/stories/soften-theme`, { theme, category, language, hint }),
};

// Shared/Public Stories API (no auth required)
export const sharedStoriesApi = {
  get: (shareToken: string): Promise<AxiosResponse<PublicStoryResponse>> => axios.get(`${API_URL}/shared/${shareToken}`),
  getPageAudio: (shareToken: string, pageNumber: number, voice = 'nova'): Promise<AxiosResponse<{ audio_url: string }>> =>
    axios.post(`${API_URL}/shared/${shareToken}/pages/${pageNumber}/audio?voice=${voice}`),
};

// Seed story search pages (2026-09-30, backend/routes/seo_pages.py): the SPA side of /<lang>/<section>/<slug>.
export interface SeoAlternate { language: string; title: string; slug: string; path: string }
export interface SeoStory {
  id: string; title: string; language: string; slug: string; path: string; index_path: string;
  share_token: string; target_age: number | null; moral: string | null; alternates: SeoAlternate[];
  head: { title: string; description: string };
  /** Trial texts in the story's language: nameless premise, `{Name}` template, the slide's one line. */
  premise: string; named_premise: string; slide_sub: string;
  /** The whole text, the neighbours and the section labels (story language) for the text under the reader. */
  pages: string[];
  related: { title: string; path: string }[];
  labels: { full_text: string; more: string; also_in: string; meta: string; based_on: string | null;
    /** "Watch the read-aloud on YouTube" / its short header form (2026-10-03); absent on older servers. */
    youtube?: string; youtube_short?: string };
  /** The story's public read-aloud video (stories.youtube_url, 2026-10-03), else null; shown on the web only. */
  youtube_url?: string | null;
}
export interface SeoIndexStory { id: string; title: string; slug: string; path: string; reading_time: number; target_age: number | null }
export const seoApi = {
  story: (lang: string, slug: string): Promise<AxiosResponse<SeoStory>> => axios.get(`${API_URL}/seo/${lang}/${slug}`),
  index: (lang: string): Promise<AxiosResponse<{ language: string; section: string; h1: string; intro: string; head: { title: string; description: string }; stories: SeoIndexStory[] }>> =>
    axios.get(`${API_URL}/seo/${lang}`),
};

// Anonymous trial stories (no auth): one 5-page story, claimable later
export const anonStoriesApi = {
  /** `fresh` bypasses the 60 s HTTP cache (the closing slide asks for the current pool stock). */
  status: (opts?: { fresh?: boolean }): Promise<AxiosResponse<AnonStatus>> =>
    axios.get(`${API_URL}/anon/status`, opts?.fresh ? { params: { t: Date.now() } } : undefined),
  create: (data: AnonStoryCreateData): Promise<AxiosResponse<AnonStoryResponse>> => axios.post(`${API_URL}/anon/stories`, data),
  // Mail the claim link of the own trial story to the parent (2026-09-16); the address is not stored.
  sendClaimLink: (storyId: string, claimToken: string, email: string): Promise<AxiosResponse<{ sent: boolean }>> =>
    axios.post(`${API_URL}/anon/stories/${storyId}/claim-link`, { claim_token: claimToken, email, website: '' }),
  /** Folge 3 teaser (2026-10-15, lib/trialTeaser.ts): the next episode of the own finished trial — made on the first
   *  call, its state on later ones. The claim token travels in the body, never in the URL. */
  next: (storyId: string, claimToken: string): Promise<AxiosResponse<TeaserView>> =>
    axios.post(`${API_URL}/anon/stories/${storyId}/next`, { claim_token: claimToken }),
  /** „Erinnere mich morgen Abend“ (2026-10-02): stores a pending reminder and mails the double-opt-in link; the
   *  address is kept until the one reminder is sent (≤ 48 h). `send_at` = the reminder time (UTC ISO). */
  requestReminder: (storyId: string, claimToken: string, email: string): Promise<AxiosResponse<{ pending: boolean; send_at: string }>> =>
    axios.post(`${API_URL}/anon/stories/${storyId}/reminder`, { claim_token: claimToken, email, website: '' }),
  /** The recovery dialog's "Automatisch anpassen" for anonymous surfaces (2026-09-06); `website` is the honeypot. */
  softenTheme: (theme: string, category: string | null, language: string, child_age: number | null, website: string, hint: string | null = null): Promise<AxiosResponse<{ softened: string }>> =>
    axios.post(`${API_URL}/anon/soften-theme`, { theme, category, language, child_age, website, hint }),
  /** The app's import card (2026-09-26): code → the story's link + title. Never the claim token — the claim
   *  happens at sign-in with the code (backend auth._maybe_claim_story). */
  resolveCode: (code: string): Promise<AxiosResponse<{ story_id: string; share_token: string; title: string; language: string; expires_at: string | null }>> =>
    axios.post(`${API_URL}/anon/claim-code/resolve`, { code, website: '' }),
};

/** A content-screen decline the recovery dialog can act on (2026-09-06). */
export interface Decline {
  message: string;
  category: string;
  /** The offending phrase as typed — the brand from the trademark layer, or the screener's verbatim excerpt (2026-09-12); null when the model gave none. */
  matched: string | null;
  /** The screener's one-line reason in the parent's language (2026-09-12); null for deterministic declines. */
  reason: string | null;
}

/**
 * Both create routes answer a decline with 400 `{detail, category, matched}` — `detail`
 * stays the string every older client reads, the two siblings drive the dialog.
 * Anything else (429 with `error`, 422 with a list) is not a decline: null.
 */
export function declineFromError(error: unknown): Decline | null {
  if (!error || typeof error !== 'object' || !('response' in error)) return null;
  const r = (error as { response?: { status?: number; data?: { detail?: unknown; category?: unknown; matched?: unknown; reason?: unknown } } }).response;
  if (r?.status !== 400 || typeof r.data?.category !== 'string') return null;
  return {
    message: typeof r.data.detail === 'string' ? r.data.detail : '',
    category: r.data.category,
    matched: typeof r.data.matched === 'string' && r.data.matched ? r.data.matched : null,
    reason: typeof r.data.reason === 'string' && r.data.reason ? r.data.reason : null,
  };
}

interface LibraryParams {
  page?: string;
  page_size?: string;
  keyword?: string;
  reading_time?: string;
  language?: string;
  age_group?: string;
}

// Public Library API
export const libraryApi = {
  getStories: (params: LibraryParams = {}): Promise<AxiosResponse<PublicLibraryResponse>> => {
    // The anonymous listing is Snugtale's only (2026-10-02): belt and braces with the backend default, so SnugFable
    // stories never reach the signed-out shelf (docs/NATIVE-FIRST-RUN-2026-10.md, Phase 0 step 2).
    const searchParams = new URLSearchParams({ app_id: 'snugtale' });
    if (params.page) searchParams.set('page', params.page);
    if (params.page_size) searchParams.set('page_size', params.page_size);
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.reading_time) searchParams.set('reading_time', params.reading_time);
    if (params.language) searchParams.set('language', params.language);
    if (params.age_group) searchParams.set('age_group', params.age_group);
    return axios.get(`${API_URL}/library?${searchParams.toString()}`);
  },
  getStoriesAuth: (params: LibraryParams = {}): Promise<AxiosResponse<PublicLibraryResponse>> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page);
    if (params.page_size) searchParams.set('page_size', params.page_size);
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.reading_time) searchParams.set('reading_time', params.reading_time);
    if (params.language) searchParams.set('language', params.language);
    if (params.age_group) searchParams.set('age_group', params.age_group);
    return axios.get(`${API_URL}/library/auth?${searchParams.toString()}`);
  },
  vote: (storyId: string, vote: number): Promise<AxiosResponse<VoteResponse>> => axios.post(`${API_URL}/library/${storyId}/vote`, { vote }),
};

import type { VoteResponse } from './types';

// Config API
export const configApi = {
  getStyles: (): Promise<AxiosResponse<{ styles: StyleOption[] }>> => axios.get(`${API_URL}/config/styles?app_id=snugtale`),
  getVoices: (): Promise<AxiosResponse<{ voices: unknown[] }>> => axios.get(`${API_URL}/config/voices`),
  getPageCounts: (): Promise<AxiosResponse<{ options: PageCountOption[] }>> => axios.get(`${API_URL}/config/page-counts`),
  getMorals: (): Promise<AxiosResponse<{ options: string[] }>> => axios.get(`${API_URL}/config/morals?app_id=snugtale`),
  getIdeas: (language: string, length = 'short', interests: string[] = [], context = '', childName = '', theme = '', childHero = false, surface = ''): Promise<AxiosResponse<{ ideas: string[] }>> => {
    const params = new URLSearchParams({ language, length, app_id: 'snugtale' });
    if (surface) params.set('surface', surface); // cost attribution only: which screen asked (account | trial | slide)
    if (interests.length) params.set('interests', interests.join(','));
    if (context.trim()) params.set('context', context.trim());
    if (childName.trim()) params.set('child_name', childName.trim().slice(0, 50)); // the ideas star the child
    if (theme.trim()) params.set('theme', theme.trim().slice(0, 500)); // the model keeps a child named in the parent's text
    if (childHero) params.set('hero', 'child'); // trial surfaces: the hero is a child ("das Kind" or the named one), never a named animal
    return axios.get(`${API_URL}/config/ideas?${params.toString()}`);
  },
  getProviderStatus: (): Promise<AxiosResponse<{ status: string; message: string | null }>> => axios.get(`${API_URL}/provider-status`),
  getAuthConfig: (): Promise<AxiosResponse<AuthConfigResponse>> => axios.get(`${API_URL}/config/auth?app_id=snugtale`),
};

// Audio API
export const audioApi = {
  generate: (text: string, voice = 'nova'): Promise<AxiosResponse<{ audio_url: string }>> =>
    axios.post(`${API_URL}/generate/audio`, { text, voice }),
};

// Purchases API
export const purchasesApi = {
  getBalance: (): Promise<AxiosResponse<BalanceResponse>> => axios.get(`${API_URL}/balance`),
  getHistory: (limit = 50, offset = 0): Promise<AxiosResponse<PurchaseHistoryResponse>> =>
    axios.get(`${API_URL}/purchases/history?limit=${limit}&offset=${offset}`),
  /** Has RevenueCat's webhook credited this store transaction yet? Never credits (2026-10-20). `final` = the app's
   *  last poll: the server logs a still-pending purchase as an ERROR. */
  validate: (receiptId: string, productId: string, platform: string, final = false): Promise<AxiosResponse<PurchaseValidateResponse>> =>
    axios.post(`${API_URL}/purchases/validate`, { receipt_id: receiptId, product_id: productId, platform, ...(final ? { final: true } : {}) }),
};

export const dashboardApi = {
  submitPageRead: (data: PageReadSubmit): Promise<AxiosResponse<PageReadResponse>> =>
    axios.post(`${API_URL}/page-reads`, data),
  submitQuizAnswers: (data: QuizAnswerSubmit): Promise<AxiosResponse<{ stored: number; reading_mode: string | null }>> =>
    axios.post(`${API_URL}/quiz-answers`, data),
  submitDiscussionViewed: (storyId: string): Promise<AxiosResponse<void>> =>
    axios.post(`${API_URL}/discussion-viewed`, { story_id: storyId }),
  getDashboard: (): Promise<AxiosResponse<DashboardData>> =>
    axios.get(`${API_URL}/dashboard`),
};

export const authApi = {
  verifyEmail: (token: string) =>
    axios.post(`${API_URL}/auth/verify-email`, { token }),
  resendVerification: () =>
    axios.post(`${API_URL}/auth/resend-verification`),
  forgotPassword: (email: string, appId: string = 'snugtale') =>
    axios.post(`${API_URL}/auth/forgot-password`, { email, app_id: appId }),
  resetPassword: (token: string, newPassword: string) =>
    axios.post(`${API_URL}/auth/reset-password`, { token, new_password: newPassword }),
  deleteAccount: () => axios.delete(`${API_URL}/auth/account`),
  // Native system-browser login: redeem the one-time code from snugtale://auth/callback (2026-09-18).
  handoffExchange: (code: string, codeVerifier: string): Promise<AxiosResponse<TokenResponse>> =>
    axios.post(`${API_URL}/auth/handoff/exchange`, { code, code_verifier: codeVerifier }),
  googleLogin: (idToken: string, opts: { language?: string; childAge?: number | null; claimToken?: string | null } = {}): Promise<AxiosResponse<TokenResponse>> =>
    axios.post(`${API_URL}/auth/google`, { id_token: idToken, app_id: 'snugtale', language: opts.language, child_age: opts.childAge ?? undefined, claim_token: opts.claimToken ?? undefined }),
  appleLogin: (identityToken: string, opts: { authorizationCode?: string | null; language?: string; childAge?: number | null; claimToken?: string | null } = {}): Promise<AxiosResponse<TokenResponse>> =>
    axios.post(`${API_URL}/auth/apple`, { identity_token: identityToken, authorization_code: opts.authorizationCode ?? undefined, app_id: 'snugtale', language: opts.language, child_age: opts.childAge ?? undefined, claim_token: opts.claimToken ?? undefined }),
};

// Moderation: report content, block users. Closes Apple 1.2 + Play UGC.
export interface ReportBody {
  content_type: 'story' | 'portrait' | 'pet_photo' | 'user';
  content_id: string;
  reason: 'inappropriate' | 'offensive' | 'spam' | 'copyright' | 'child_safety' | 'other';
  notes?: string;
  /** Story reports: the page (1-based) on screen in the reader (2026-10-18); the server checks it against the story. */
  page?: number;
}

export interface BlockedUserItem {
  blocked_id: string;
  display_name: string;
  created_at: string;
}

/** A report without an account (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md step 4): stories only. */
export interface AnonReportBody {
  content_type: 'story';
  content_id: string;
  reason: ReportBody['reason'];
  notes?: string;
  page?: number;
}

export const moderationApi = {
  report: (body: ReportBody): Promise<AxiosResponse<{ success: boolean }>> =>
    axios.post(`${API_URL}/reports`, body),
  /** Signed-out reporting (2026-10-02): the reader's Report flag on the trial story, shared links and the library
   *  works without an account (Play AI-content policy: in-app reporting on the first AI content an install sees).
   *  The server keeps no ids, IP or UA (anonymous-data rules); `website` is the honeypot. 201 on success, 404 unknown
   *  story, 429 caps, 400 honeypot. */
  reportAnon: (body: AnonReportBody): Promise<AxiosResponse<{ success: boolean }>> =>
    axios.post(`${API_URL}/reports/anon`, { ...body, website: '' }),
  block: (blockedUserId: string): Promise<AxiosResponse<void>> =>
    axios.post(`${API_URL}/blocks`, { blocked_user_id: blockedUserId }),
  unblock: (blockedUserId: string): Promise<AxiosResponse<void>> =>
    axios.delete(`${API_URL}/blocks/${blockedUserId}`),
  listBlocks: (): Promise<AxiosResponse<{ blocks: BlockedUserItem[] }>> =>
    axios.get(`${API_URL}/blocks`),
};

export default {
  stories: storiesApi,
  config: configApi,
  audio: audioApi,
  purchases: purchasesApi,
  dashboard: dashboardApi,
  auth: authApi,
  moderation: moderationApi,
};
